/**
 * Board 7 · History: every promotion event of the protocol, newest first, filtered by switches, settings, and
 * system and alerts. Settings changes themselves stay in Settings › Logs, under Promotion.
 */

import { useState } from 'react';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatWhen } from './scenarios';
import { usePromotionHistory } from './use-live-promote';
import { historyEntriesOf, matchesFilter, type HistoryFilter } from './view-rules';

const FILTERS: Array<[HistoryFilter, string]> = [
	['all', 'All'],
	['switches', 'Switches'],
	['system', 'System and alerts']
];

export const HistoryView = ({ sopId, onBack }: { sopId: string; onBack: () => void }) => {
	const { data, isPending, error } = usePromotionHistory(sopId);
	const [filter, setFilter] = useState<HistoryFilter>('all');
	const entries = historyEntriesOf(data ?? []).filter((entry) => matchesFilter(entry, filter));
	return (
		<div className='flex min-h-0 flex-1 flex-col'>
			<div className='flex shrink-0 items-center gap-2 border-b border-gray-500 px-4 py-3'>
				<button type='button' onClick={onBack} aria-label='Back to the scenarios' className='cursor-pointer rounded-md p-1 hover:bg-gray-100'>
					<ChevronLeft className='size-4' aria-hidden />
				</button>
				<span className='text-[15px] font-semibold text-text-heading'>History</span>
			</div>
			<div className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4'>
				<div role='group' aria-label='Filter history' className='inline-flex w-fit rounded-full bg-gray-100 p-0.5'>
					{FILTERS.map(([key, label]) => (
						<button
							key={key}
							type='button'
							aria-pressed={filter === key}
							onClick={() => setFilter(key)}
							className={cn(
								'cursor-pointer rounded-full px-3 py-1 text-[13px] font-medium',
								filter === key ? 'bg-white text-text-heading shadow-sm' : 'text-gray-800'
							)}
						>
							{label}
						</button>
					))}
				</div>
				{isPending && (
					<p className='flex items-center gap-2 text-xs text-gray-800'>
						<Loader2 className='size-3.5 animate-spin' aria-hidden /> Reading the history…
					</p>
				)}
				{error && <p className='text-xs text-gray-800'>Could not read the history.</p>}
				{!isPending && !entries.length && <p className='text-xs text-gray-800'>Nothing here yet.</p>}
				<ol className='flex flex-col'>
					{entries.map((entry, index) => (
						<li key={`${entry.at}-${index}`} className='flex gap-3 border-b border-gray-300 py-2.5 last:border-b-0'>
							<span className='w-28 shrink-0 text-xs text-gray-800'>
								<span className='block font-medium text-text-heading'>{entry.by === null ? 'System' : (entry.byName ?? 'Someone')}</span>
								{formatWhen(entry.at, true)}
							</span>
							<span className='text-[13px] text-gray-900'>{entry.detail}</span>
						</li>
					))}
				</ol>
				<p className='text-xs text-gray-800'>Settings changes are in Settings › Logs, under Promotion.</p>
			</div>
		</div>
	);
};
